import { useCallback, useSyncExternalStore } from 'react';

/** The breakpoint between the handoff's two layouts (ADR-0004). */
export const DESKTOP_QUERY = '(min-width: 900px)';

/**
 * Tracks a media query.
 *
 * `useSyncExternalStore` rather than `useState` + `useEffect`: matchMedia is an external
 * store, and subscribing to it this way avoids the extra render an effect-then-setState
 * pattern causes on mount, and avoids tearing during concurrent renders.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const list = window.matchMedia(query);
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    [query],
  );

  const getSnapshot = useCallback(() => window.matchMedia(query).matches, [query]);

  // Server snapshot: the mobile layout is the safe default, since it is the narrower one.
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

/** True on the desktop layout: top source bar, canvas well, right sidebar. */
export const useIsDesktop = (): boolean => useMediaQuery(DESKTOP_QUERY);
